import React, { useState } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4 shadow-sm">
      <div className="text-blue-800 font-bold mb-3">{title}</div>
      {children}
    </div>
  );
}
const Inp = ({ v, on, w = 20 }) => (
  <input type="number" step="any" value={v} onChange={e => on(e.target.value === '' ? '' : +e.target.value)}
    className={`w-${w} px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 focus:bg-white focus:border-blue-500 outline-none text-center`} />
);

export default function PercentageCalc() {
  return (
    <CalcShell look="cnet"
      title="Percentage Calculator"
      subtitle="Multiple percentage calculators — find %, whole, part, change, tip, and percent difference."
      about={<>
        <p><b>What is a percentage?</b> A percentage is a fraction expressed out of 100. It is denoted using the symbol %. For example, 25% = 25/100 = 0.25.</p>
        <p>Below are eight of the most common percentage calculation types. They cover everyday problems like discounts, grades, tips, and comparisons.</p>
      </>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card1 /><Card2 /><Card3 /><Card4 />
        <Card5 /><Card6 /><Card7 /><Card8 />
      </div>
      <div data-inputs-end />
      <StepsPanel steps={[
        `<b>Percent of a number:</b> Part = Whole × Percent ÷ 100`,
        `<b>What % is A of B:</b> Percent = A ÷ B × 100`,
        `<b>Percent change:</b> ((New − Old) ÷ Old) × 100`,
        `<b>Percent difference:</b> (|A − B| ÷ ((A + B)/2)) × 100`
      ]} />
    </CalcShell>
  );
}

function Card1() {
  const [p, setP] = useState(20); const [v, setV] = useState(150);
  return (
    <Section title="What is P% of V?">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <span>What is</span><Inp v={p} on={setP} w="20" /><span>% of</span><Inp v={v} on={setV} w="24" /><span>=</span>
        <span className="font-bold text-lg text-blue-700">{typeof p === 'number' && typeof v === 'number' ? (p*v/100).toLocaleString() : '—'}</span>
      </div>
    </Section>
  );
}
function Card2() {
  const [a, setA] = useState(30); const [b, setB] = useState(120);
  return (
    <Section title="A is what % of B?">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <Inp v={a} on={setA} w="24" /><span>is what % of</span><Inp v={b} on={setB} w="24" /><span>=</span>
        <span className="font-bold text-lg text-blue-700">{typeof a === 'number' && typeof b === 'number' && b ? (a/b*100).toFixed(2) + '%' : '—'}</span>
      </div>
    </Section>
  );
}
function Card3() {
  const [a, setA] = useState(12); const [p, setP] = useState(30);
  return (
    <Section title="A is P% of what?">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <Inp v={a} on={setA} w="24" /><span>is</span><Inp v={p} on={setP} w="20" /><span>% of</span>
        <span className="font-bold text-lg text-blue-700">{typeof a === 'number' && typeof p === 'number' && p ? (a*100/p).toLocaleString() : '—'}</span>
      </div>
    </Section>
  );
}
function Card4() {
  const [from, setFrom] = useState(50); const [to, setTo] = useState(75);
  const ch = typeof from === 'number' && typeof to === 'number' && from ? ((to-from)/from)*100 : null;
  return (
    <Section title="Percent Change A → B">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <span>From</span><Inp v={from} on={setFrom} w="24" /><span>to</span><Inp v={to} on={setTo} w="24" /><span>=</span>
        <span className="font-bold text-lg" style={{ color: ch === null ? '#64748b' : ch >= 0 ? '#059669' : '#dc2626' }}>{ch === null ? '—' : (ch >= 0 ? '+' : '') + ch.toFixed(2) + '%'}</span>
      </div>
    </Section>
  );
}
function Card5() {
  const [a, setA] = useState(80); const [b, setB] = useState(120);
  const diff = typeof a === 'number' && typeof b === 'number' && a + b ? Math.abs(a-b) / ((a+b)/2) * 100 : null;
  return (
    <Section title="Percent Difference between A & B">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <span>A</span><Inp v={a} on={setA} w="24" /><span>B</span><Inp v={b} on={setB} w="24" /><span>=</span>
        <span className="font-bold text-lg text-blue-700">{diff === null ? '—' : diff.toFixed(2) + '%'}</span>
      </div>
    </Section>
  );
}
function Card6() {
  const [amt, setAmt] = useState(200); const [tip, setTip] = useState(15);
  return (
    <Section title="Tip Calculator">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <span>Bill $</span><Inp v={amt} on={setAmt} w="24" /><span>tip</span><Inp v={tip} on={setTip} w="16" />%
      </div>
      <div className="mt-2 text-sm text-slate-700">Tip: <b>${(amt*tip/100).toFixed(2)}</b> · Total: <b className="text-blue-700">${(amt*(1+tip/100)).toFixed(2)}</b></div>
    </Section>
  );
}
function Card7() {
  const [orig, setOrig] = useState(100); const [disc, setDisc] = useState(25);
  return (
    <Section title="Discount / Sale Price">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <span>$</span><Inp v={orig} on={setOrig} w="24" /><span>with</span><Inp v={disc} on={setDisc} w="16" />% off
      </div>
      <div className="mt-2 text-sm text-slate-700">Save: <b>${(orig*disc/100).toFixed(2)}</b> · Final: <b className="text-blue-700">${(orig*(1-disc/100)).toFixed(2)}</b></div>
    </Section>
  );
}
function Card8() {
  const [correct, setCorrect] = useState(85); const [total, setTotal] = useState(100);
  return (
    <Section title="Test Grade">
      <div className="flex flex-wrap items-center gap-2 text-slate-800">
        <Inp v={correct} on={setCorrect} w="20" /><span>correct out of</span><Inp v={total} on={setTotal} w="20" />
      </div>
      <div className="mt-2 text-sm text-slate-700">Score: <b className="text-blue-700">{total ? (correct/total*100).toFixed(2) + '%' : '—'}</b></div>
    </Section>
  );
}
